import { DateTime } from '@ksyos/date-time';
import { Alert, Badge } from '@ksyos/design-system';
import type { ActivityType, Profession, ServiceCode } from '@common/enums';
import type { CompensationPayoutForServiceActivity } from '@common/validation/compensationPayoutForServiceActivity';
import type { ICompensationPayout } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { doesPayoutDependOnProfession } from '@common/utils/doesPayoutDependOnProfession';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { Currency } from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { hasOneAuthorization } from '@frontend/utils';

export function getCompensationEndDate(
    compensationPayoutEndDate: string | Date | null,
    rightEndDate: string | Date | undefined
) {
    if (compensationPayoutEndDate) {
        return <NullableFormattedDate value={compensationPayoutEndDate} />;
    } else if (rightEndDate) {
        return <NullableFormattedDate value={rightEndDate} />;
    } else {
        return '-';
    }
}

export function getOverlappingMessage(
    higherLevelCompensationPayout?: ICompensationPayout | CompensationPayoutForServiceActivity,
    link?: string
) {
    if (!higherLevelCompensationPayout || !link) {
        return null;
    }

    return (
        <Alert appearance="info">
            {'De onderstaande vergoedingen worden genegeerd. De vergoeding is namelijk op een hoger niveau ingesteld ('}
            {getPayoutLevel(higherLevelCompensationPayout, link)}
            {') op '}
            <Currency value={higherLevelCompensationPayout.amount} />
            {' vanaf '}
            {DateTime.from(higherLevelCompensationPayout.startDate).format('DD-MM-YYYY')}
            {higherLevelCompensationPayout.endDate
                ? ' tot ' + DateTime.from(higherLevelCompensationPayout.endDate).format('DD-MM-YYYY')
                : ''}
            .
        </Alert>
    );
}

export function getPayoutLevel(
    higherLevelCompensationPayout: ICompensationPayout | CompensationPayoutForServiceActivity,
    link: string
) {
    return (higherLevelCompensationPayout.serviceGroupSettingId &&
        hasOneAuthorization(ManagerAuthorization.groupRead)) ||
        (!higherLevelCompensationPayout.serviceGroupSettingId &&
            hasOneAuthorization(ManagerAuthorization.organizationRead)) ? (
        <Alert.Link
            to={link}
            variant="link"
        >
            {getPayoutLevelName(higherLevelCompensationPayout)}
        </Alert.Link>
    ) : (
        getPayoutLevelName(higherLevelCompensationPayout)
    );
}

export function getPayoutLevelName(
    higherLevelCompensationPayout: ICompensationPayout | CompensationPayoutForServiceActivity
) {
    return higherLevelCompensationPayout.serviceGroupSettingId ? 'groepsniveau' : 'organisatieniveau';
}

export function getBadge({
    activityType,
    compensationPayout,
    rightStartDate,
    rightEndDate,
    higherLevelCompensationPayout,
    organizationLevelPayoutsStartDate,
    serviceCode,
    isOnOrganizationLevel,
    profession,
}: {
    activityType?: ActivityType | null;
    compensationPayout: CompensationPayoutForServiceActivity | ICompensationPayout;
    higherLevelCompensationPayout?: CompensationPayoutForServiceActivity | ICompensationPayout;
    isOnOrganizationLevel?: boolean;
    organizationLevelPayoutsStartDate?: string;
    profession?: Profession | null;
    rightEndDate?: string | Date;
    rightStartDate?: string | Date;
    serviceCode?: ServiceCode;
}) {
    const translate = getLanguage();

    if (
        isActiveOnDate(compensationPayout, new Date()) &&
        isActiveOnDate({ rightEndDate, startDate: rightStartDate || null }, new Date())
    ) {
        if (
            isCompensationPayoutIgnored({
                activityType,
                higherLevelCompensationPayout,
                isOnOrganizationLevel,
                organizationLevelPayoutsStartDate,
                profession,
                serviceCode,
            })
        ) {
            return (
                <Badge
                    color="orange"
                    text={translate.compensationPayouts.ignored}
                ></Badge>
            );
        } else {
            return (
                <Badge
                    color="green"
                    text={translate.compensationPayouts.current}
                ></Badge>
            );
        }
    } else if (DateTime.now().isBefore(DateTime.from(compensationPayout.startDate))) {
        return;
    }

    return (
        <Badge
            color="neutral"
            text={translate.common.date_last}
        ></Badge>
    );
}

function isCompensationPayoutIgnored({
    activityType,
    higherLevelCompensationPayout,
    organizationLevelPayoutsStartDate,
    serviceCode,
    isOnOrganizationLevel,
    profession,
}: {
    activityType?: ActivityType | null;
    higherLevelCompensationPayout?: CompensationPayoutForServiceActivity | ICompensationPayout;
    isOnOrganizationLevel?: boolean;
    organizationLevelPayoutsStartDate?: string;
    profession?: Profession | null;
    serviceCode?: ServiceCode;
}) {
    const isPayoutNotOnProfessionLevelWhileItShouldBeOnProfessionLevel =
        organizationLevelPayoutsStartDate &&
        doesPayoutDependOnProfession(serviceCode, activityType) &&
        DateTime.today().isSameOrAfter(organizationLevelPayoutsStartDate) &&
        ((isOnOrganizationLevel && !profession) || !isOnOrganizationLevel);

    const isPayoutOnProfessionWhileItShouldBeOnOrganizationLevel =
        profession &&
        (!organizationLevelPayoutsStartDate || DateTime.today().isBefore(organizationLevelPayoutsStartDate));

    return (
        isPayoutNotOnProfessionLevelWhileItShouldBeOnProfessionLevel ||
        isPayoutOnProfessionWhileItShouldBeOnOrganizationLevel ||
        higherLevelCompensationPayout
    );
}
